import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Send, Eye, DollarSign, Wallet, Sparkles, PlayCircle, ShieldCheck,
  TrendingUp, ChevronRight, Clock, Video,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import TikTokEmbed from '../../components/TikTokEmbed';
import { getPublicSettings, clipperPublicStats, getStoredClipperToken } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { LanguageToggle } from '../../components/LanguageToggle';

const SITE_LOGO =
  'https://customer-assets.emergentagent.com/job_87cd0f70-4f18-4348-a2fd-e2666a61734d/artifacts/ozrawt0t_IMG_0439.jpeg';

function StatPill({ Icon, value, label, testid }) {
  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-center"
      data-testid={testid}
    >
      <Icon className="h-5 w-5 text-[hsl(43_92%_56%)] mx-auto mb-2" />
      <div className="font-display text-2xl sm:text-3xl text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function StepCard({ n, title, body, Icon }) {
  return (
    <Card className="card-surface">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-[rgba(245,185,66,0.10)] border border-[hsl(43_92%_56%)]/40 flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4 text-[hsl(43_92%_56%)]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Step {n}</div>
            <div className="font-display text-lg mt-0.5">{title}</div>
            <p className="text-sm text-[hsl(0_0%_82%)] leading-relaxed mt-1">{body}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ClipLandingPage() {
  const t = useT();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const isLoggedIn = !!getStoredClipperToken();

  useEffect(() => {
    (async () => {
      try {
        const [s, st] = await Promise.all([getPublicSettings(), clipperPublicStats().catch(() => null)]);
        setSettings(s);
        setStats(st);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const rate = settings?.clip_rate_usd_per_1m ?? 40;
  const minW = settings?.clip_min_withdrawal_usd ?? 10;
  const tg = settings?.clip_source_telegram_link || 'https://t.me/alveefxcliping';
  const title = settings?.clip_hero_title || t('Earn $40 per 1M views — Clip AlveeFx Content').replace('$40', `$${rate}`);
  const subtitle = settings?.clip_hero_subtitle ||
    t('Make short clips from official AlveeFx videos, post them on TikTok / Instagram / Facebook, and get paid in USDT. We track your views automatically every few hours.');
  const enabled = settings?.clip_program_enabled !== false;

  return (
    <main className="min-h-screen text-white bg-[hsl(0_0%_4%)] relative noise-overlay" data-testid="clip-landing">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(60% 60% at 50% 0%, rgba(245,185,66,0.16), transparent 70%), radial-gradient(50% 50% at 80% 100%, rgba(225,29,46,0.12), transparent 70%)',
        }}
      />
      <header className="relative z-10 border-b border-white/5">
        <div className="container-x py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={SITE_LOGO} alt="AlveeFx" className="h-8 w-8 rounded-full object-cover ring-1 ring-[rgba(245,185,66,0.42)]" />
            <span className="font-display uppercase tracking-[0.18em] text-sm sm:text-base">AlveeFx</span>
            <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] text-[hsl(43_92%_72%)] px-2 py-0.5 rounded-full border border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.06)]">{t('Clip Program')}</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            {isLoggedIn ? (
              <Link to="/clip/dashboard" data-testid="nav-dashboard">
                <Button size="sm" variant="outline" className="border-white/10 bg-white/[0.02] h-9">
                  {t('Dashboard')} <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/clip/login" data-testid="nav-login">
                  <Button size="sm" variant="outline" className="border-white/10 bg-white/[0.02] h-9">{t('Log in')}</Button>
                </Link>
                <Link to="/clip/signup" data-testid="nav-signup">
                  <Button size="sm" className="cta-sheen border-0 text-white font-semibold h-9 gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> {t('Start clipping')}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative z-10 py-14 sm:py-20">
        <div className="container-x max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="text-center">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[rgba(245,185,66,0.42)] bg-[rgba(245,185,66,0.08)] mb-4">
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(43_92%_56%)] animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)] font-semibold">{t('Open · Instant Approval')}</span>
            </div>
            <h1 className="font-display uppercase text-3xl sm:text-5xl lg:text-6xl leading-[1.02]" data-testid="clip-hero-title">{title}</h1>
            <p className="mt-5 text-base sm:text-lg text-[hsl(0_0%_82%)] leading-relaxed max-w-2xl mx-auto">{subtitle}</p>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <Link to={isLoggedIn ? '/clip/dashboard' : '/clip/signup'} data-testid="hero-cta">
                <Button size="lg" disabled={!enabled} className="cta-sheen border-0 h-12 px-7 text-base font-semibold w-full sm:w-auto">
                  <Sparkles className="h-4 w-4 mr-2" /> {isLoggedIn ? t('Open Dashboard') : t('Start clipping for free')}
                </Button>
              </Link>
              <a href={tg} target="_blank" rel="noreferrer" data-testid="hero-tg">
                <Button size="lg" variant="outline" className="h-12 px-6 border-white/10 bg-white/[0.02] w-full sm:w-auto">
                  <Send className="h-4 w-4 mr-2" /> {t('Get source videos on Telegram')}
                </Button>
              </a>
            </div>

            {!enabled && (
              <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-[hsl(0_72%_70%)] font-semibold">
                {t('Program currently paused — signups disabled')}
              </div>
            )}
          </motion.div>

          {/* Stats */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatPill Icon={DollarSign} value={`$${rate}`} label={t('per 1M views')}      testid="stat-rate" />
            <StatPill Icon={Wallet}     value={`$${minW}`} label={t('Min withdrawal')}     testid="stat-min" />
            <StatPill Icon={Eye}        value={(stats?.total_views ?? 0).toLocaleString()} label={t('Total views tracked')} testid="stat-views" />
            <StatPill Icon={TrendingUp} value={stats?.total_clippers ?? 0} label={t('Active clippers')} testid="stat-clippers" />
          </div>

          {/* How it works */}
          <div className="mt-14">
            <h2 className="font-display uppercase text-2xl sm:text-3xl text-center">{t('How it works')}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <StepCard n={1} Icon={Video} title={t('Get source videos')}
                body={t('Join our Telegram channel for the latest AlveeFx clips, raw footage, and brand assets. All free to remix.')} />
              <StepCard n={2} Icon={PlayCircle} title={t('Edit & post')}
                body={t('Cut a short clip (15–60s recommended), add hooks and captions, post to TikTok, Instagram Reels, or Facebook Reels.')} />
              <StepCard n={3} Icon={Eye} title={t('Submit your URL')}
                body={t('On your dashboard, paste the URL of your post. We automatically scan view counts every few hours.')} />
              <StepCard n={4} Icon={Wallet} title={t('Withdraw in USDT')}
                body={t('Earn $40 per 1M views').replace('$40', `$${rate}`) + ` — ${t('Min withdrawal')} $${minW}.`} />
            </div>
          </div>

          {/* Reference videos — "See it in action" */}
          {Array.isArray(settings?.clip_reference_videos) && settings.clip_reference_videos.length > 0 && (
            <div className="mt-14" data-testid="reference-videos">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.06)] mb-3">
                  <PlayCircle className="h-3 w-3 text-[hsl(43_92%_56%)]" />
                  <span className="text-[10px] uppercase tracking-[0.28em] text-[hsl(43_92%_56%)] font-semibold">{t('See it in action')}</span>
                </div>
                <h2 className="font-display uppercase text-2xl sm:text-3xl">{t('Example AlveeFx clips')}</h2>
                <p className="mt-2 text-sm text-[hsl(0_0%_82%)] max-w-xl mx-auto">
                  {t('Quick reference of the kind of edits that perform well. Tap to play in-place.')}
                </p>
              </div>
              <div className="mt-6 grid gap-5 md:grid-cols-2">
                {settings.clip_reference_videos.slice(0, 4).map((v, i) => (
                  <div key={v.video_id || i} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 sm:p-4">
                    <TikTokEmbed
                      videoId={v.video_id}
                      url={v.url}
                      title={v.title || `AlveeFx clip #${i + 1}`}
                    />
                    {v.title && (
                      <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground text-center">
                        {v.title}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trust strip */}
          <div className="mt-12 grid gap-3 sm:grid-cols-3 text-center">
            {[
              { Icon: ShieldCheck, label: t('Open & transparent payouts') },
              { Icon: Clock,       label: t('Views refresh every ~6 hours') },
              { Icon: Wallet,      label: t('USDT BEP-20 withdrawals') },
            ].map((tItem, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex items-center justify-center gap-2 text-xs text-[hsl(0_0%_82%)]">
                <tItem.Icon className="h-4 w-4 text-[hsl(43_92%_56%)]" />
                {tItem.label}
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div className="mt-14 grid gap-3 sm:grid-cols-2">
            {[
              { q: t('Which platforms count?'),
                a: t('TikTok, Instagram Reels, and Facebook Reels. All public posts only. Views from private accounts or shadow-banned posts will not be tracked.') },
              { q: t('How fast do views update?'),
                a: t('Every ~6 hours we re-check all your live clips. You can also manually refresh on your dashboard.') },
              { q: t('Can someone steal my clip URL?'),
                a: t('No — each URL can only be submitted by one clipper. First come, first served. Duplicates are rejected.') },
              { q: t('When is the first payout?'),
                a: `${t('Minimum payout')}: $${minW}. ${t('USDT BEP-20 withdrawals')}.` },
            ].map((f, i) => (
              <Card key={i} className="card-surface">
                <CardContent className="p-4">
                  <div className="text-sm font-semibold text-white">{f.q}</div>
                  <div className="text-xs text-[hsl(0_0%_82%)] leading-relaxed mt-1">{f.a}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-14 rounded-2xl border border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.05)] p-6 sm:p-8 text-center">
            <h3 className="font-display uppercase text-2xl sm:text-3xl">{t('Ready to start earning?')}</h3>
            <p className="mt-2 text-sm text-[hsl(0_0%_82%)] max-w-xl mx-auto">
              {t("Sign up takes 30 seconds — no admin approval, no waiting. Plug in your USDT wallet and you're live.")}
            </p>
            <Link to={isLoggedIn ? '/clip/dashboard' : '/clip/signup'} className="inline-block mt-5" data-testid="bottom-cta">
              <Button size="lg" disabled={!enabled} className="cta-sheen border-0 h-12 px-7 text-base font-semibold">
                <Sparkles className="h-4 w-4 mr-2" /> {isLoggedIn ? t('Open Dashboard') : t('Create my clipper account')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 py-5 text-center">
        <p className="text-[11px] text-muted-foreground max-w-2xl mx-auto px-4 leading-snug">
          {t('AlveeFx Clip Program · Earnings vary with platform performance · We reserve the right to remove clips that violate platform ToS or our content guidelines.')}
        </p>
      </footer>
    </main>
  );
}
